import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import {
  RenderedInlinePart,
  RenderedPart,
  renderThrough,
  type RenderElement,
} from '../../internal/renderedPart.tsx';

/**
 * The row of category words under a storefront's header, and the panel each
 * word opens.
 *
 *   <MegamenuItem ref={itemRef} onPointerEnter={…} onPointerLeave={…}>
 *     <MegamenuTrigger
 *       variant="category"
 *       inset="leading"
 *       id={triggerId}
 *       aria-controls={panelId}
 *       aria-expanded={isOpen}
 *       data-state={state}
 *       render={<Link href={taxon.routerUrl} title={title} />}
 *     >
 *       <MegamenuTriggerLabel>{label}</MegamenuTriggerLabel>
 *       <MegamenuIndicator variant="category" />
 *     </MegamenuTrigger>
 *
 *     <MegamenuPanel id={panelId} data-state={state} aria-label={panelLabel}>
 *       {whatever this shop lays out inside its panel}
 *       <MegamenuClose aria-label={closeLabel} onClick={close}><X /></MegamenuClose>
 *     </MegamenuPanel>
 *   </MegamenuItem>
 *
 * **What is here is the mechanism and the typography, never the arrangement.**
 * The word/panel pairing, the sliver that reads the trigger's state, the
 * full-bleed panel that stays in the served markup while closed, the dismiss
 * control — plus the handful of appearances a panel's copy takes. Every shop
 * needs those and needs them the same way.
 *
 * **What a panel contains is the shop's, and lives in the shop's package.**
 * How many columns the field has, whether a tile puts its mark beside the title
 * or above it, how wide the second half is, which colour "and everything else"
 * is — those read as shared until a second shop arrives, and then every one of
 * them is a number belonging to the first. rcprofi lays its panel out in
 * `component/server/navigation/desktop/megamenuLayout.tsx`; RacingLine lays its
 * own out differently, from the same parts here.
 *
 * Server-first throughout: every part is a plain component, and the open state
 * arrives as `data-state` from whatever client composition owns it —
 * `useMegamenuDisclosure` in this package is the one such piece of client code.
 */

/* -------------------------------------------------------------------------- */
/* The row                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * One word of the row, and the panel behind it.
 *
 * Paints nothing: the row's own run and spacing belong to the list, and the
 * word's box belongs to the trigger. What this owns is the pair — a hover that
 * covers the word AND the panel it opened has to be one element's hover, or the
 * panel closes the moment a pointer travels toward it.
 */
export type MegamenuItemProps = ComponentProps<'li'>;

export const MegamenuItem = ({ className, ...props }: MegamenuItemProps) => (
  <li data-slot="megamenu-item" className={clsxm(className)} {...props} />
);

/**
 * The word itself, and the one interactive element of the row.
 *
 * It is the element the page navigates with, so it carries the tab stop, the
 * role its own tag gives it and the expanded state — rather than wrapping a
 * second control that would take a second tab stop for the same target.
 *
 * **It paints no ink and sets no measure.** How large the word is, in which
 * case, on what ground and how much air it stands in are a brand's answers and
 * differ between shops; what is fixed here is that the word is a BLOCK (an
 * anchor is inline, and vertical padding on an inline box adds nothing to the
 * line) and that it carries `group`, which is what the sliver under it reads
 * its state through.
 */
const megamenuTriggerClasses = ['group relative block cursor-pointer whitespace-nowrap'];

export type MegamenuTriggerProps = ComponentProps<'a'> & {
  /** The element the page navigates with. */
  readonly render: RenderElement;
};

export const MegamenuTrigger = ({ className, render, ...props }: MegamenuTriggerProps) =>
  renderThrough(render, 'megamenu-trigger', clsxm(megamenuTriggerClasses, className), props);

/** The word's copy. A span so the trigger stays one element with one box. */
export type MegamenuTriggerLabelProps = ComponentProps<'span'>;

export const MegamenuTriggerLabel = ({ className, ...props }: MegamenuTriggerLabelProps) => (
  <span data-slot="megamenu-trigger-label" className={clsxm(className)} {...props} />
);

/**
 * The sliver under the word.
 *
 * It reads the trigger's state through the trigger's own `group`, rather than
 * being handed a boolean: the appearance is then a fact of the markup, which is
 * what lets the whole row render on the server.
 *
 * The arm says WHEN it shows, which is a mechanism — a word that opens a panel
 * marks itself while the panel is open, and a word that opens nothing has only
 * the pointer to answer to. What ink it is drawn in is the shop's, and arrives
 * as a class.
 */
export const megamenuIndicatorVariants = cva(
  ['absolute inset-x-0 -bottom-0 h-1', 'rounded-tl-full rounded-tr-full', 'opacity-0'],
  {
    variants: {
      reveal: {
        /** Shows while the word's panel is open. */
        open: 'group-data-[state=open]:opacity-100',
        /** Shows under the pointer, because there is no panel to be open. */
        hover: 'group-hover:opacity-100',
      },
    },
  }
);

export type MegamenuIndicatorProps = ComponentProps<'span'> & {
  readonly reveal: 'open' | 'hover';
};

export const MegamenuIndicator = ({ className, reveal, ...props }: MegamenuIndicatorProps) => (
  <span
    data-slot="megamenu-indicator"
    className={clsxm(megamenuIndicatorVariants({ reveal }), className)}
    {...props}
  />
);

/* -------------------------------------------------------------------------- */
/* The panel                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The full-bleed panel under the row.
 *
 * Renders its own inner run as well as the box, because that run is what the
 * close control positions against — a composition omitting it would still
 * render, with the control landing against the viewport instead.
 *
 * Hidden rather than absent: the panel's contents are in the served markup of
 * every route, which is what makes them readable to a crawler.
 *
 * It hangs at `--megamenu-row`, which is the height of the row it hangs under
 * and therefore the shop's: the row's height falls out of the word's own type
 * and padding, both of which the shop sets. The shop's `<nav>` declares the
 * variable; a panel is otherwise positioned against a row it knows nothing
 * about, and the two drift by however much the type changed.
 */
const megamenuPanelClasses = [
  'absolute inset-x-0 top-(--megamenu-row) isolate z-50 mx-auto flex max-w-(--container-3xl) justify-center',
  'w-[calc(100%-2*var(--spacing-content-safe-padding-lg))]',
  'pointer-events-none invisible opacity-0',
  'data-[state=open]:pointer-events-auto data-[state=open]:visible data-[state=open]:opacity-100',
];

/** The run inside the panel: the frame, and whatever floats over it. */
const megamenuPanelBodyClasses = 'relative flex w-full';

export type MegamenuPanelProps = ComponentProps<'div'>;

export const MegamenuPanel = ({ className, children, ...props }: MegamenuPanelProps) => (
  <div
    role="region"
    data-slot="megamenu-panel"
    className={clsxm(megamenuPanelClasses, className)}
    {...props}
  >
    <div data-slot="megamenu-panel-body" className={megamenuPanelBodyClasses}>
      {children}
    </div>
  </div>
);

/** The dismiss control in the panel's corner. Sizes whatever mark it is given. */
const megamenuCloseClasses = [
  'lap-lg:top-4 lap-lg:right-4 absolute top-2 right-2',
  '[&_svg]:text-foreground-muted lap-lg:[&_svg]:size-6 [&_svg]:size-5 [&_svg]:shrink-0',
];

export type MegamenuCloseProps = ComponentProps<'button'>;

export const MegamenuClose = ({ className, ...props }: MegamenuCloseProps) => (
  <button
    type="button"
    data-slot="megamenu-close"
    className={clsxm(megamenuCloseClasses, className)}
    {...props}
  />
);

/* -------------------------------------------------------------------------- */
/* The copy inside a panel                                                     */
/* -------------------------------------------------------------------------- */

/**
 * What a block inside the panel is called — a field of tiles, a set of brand
 * columns.
 *
 * A neutral element by default: a megamenu repeats on every route, so its
 * labels are not the page's headings and must not compete with the outline each
 * page publishes.
 */
const megamenuSectionTitleClasses = ['font-heading', 'text-lg/7 font-medium'];

export type MegamenuSectionTitleProps = ComponentProps<'span'> & {
  /** The element the page names it with, or absent for a neutral span. */
  readonly render?: RenderElement;
};

export const MegamenuSectionTitle = ({
  className,
  render,
  ...props
}: MegamenuSectionTitleProps) => (
  <RenderedInlinePart
    dataSlot="megamenu-section-title"
    className={clsxm(megamenuSectionTitleClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * The name of one group of links inside a block.
 *
 * Two arms because a group underlines its name on its own hover when the name
 * IS the link, and on the surrounding surface's hover when that surface is.
 */
export const megamenuGroupTitleVariants = cva(
  ['font-heading', 'line-clamp-2 text-lg/7 font-semibold'],
  {
    variants: {
      variant: {
        /** Underlines under the pointer that is on it. */
        linked: 'hover:underline hover:underline-offset-2',
        /** Underlines with the surface around it, which must carry `group`. */
        surface: 'group-hover:underline group-hover:underline-offset-2',
      },
    },
  }
);

export type MegamenuGroupTitleProps = ComponentProps<'span'> & {
  readonly variant: 'linked' | 'surface';
  /** The element the page names it with, or absent for a neutral span. */
  readonly render?: RenderElement;
};

export const MegamenuGroupTitle = ({
  className,
  variant,
  render,
  ...props
}: MegamenuGroupTitleProps) => (
  <RenderedInlinePart
    dataSlot="megamenu-group-title"
    className={clsxm(megamenuGroupTitleVariants({ variant }), className)}
    render={render}
    {...props}
  />
);

/**
 * A link in the panel's body — a narrower category under a tile, a brand in a
 * column. One part because they are the same muted link at two densities; the
 * arm says which density, and nothing about where it sits.
 */
export const megamenuPanelLinkVariants = cva(
  ['text-foreground-muted tracking-tight', 'hover:underline hover:underline-offset-2'],
  {
    variants: {
      variant: {
        /** Sits on the copy's own rhythm and clamps rather than pushing its row open. */
        clamped: 'line-clamp-2 text-sm/6',
        /** Carries its own tap target, for a list read as rows rather than as copy. */
        padded: 'inline-block py-1.5 pr-2 text-sm',
      },
    },
  }
);

export type MegamenuPanelLinkProps = ComponentProps<'a'> & {
  readonly variant: 'clamped' | 'padded';
  /** The element the page navigates with. */
  readonly render: RenderElement;
};

export const MegamenuPanelLink = ({
  className,
  variant,
  render,
  ...props
}: MegamenuPanelLinkProps) =>
  renderThrough(
    render,
    'megamenu-panel-link',
    clsxm(megamenuPanelLinkVariants({ variant }), className),
    props
  );

/**
 * The "and everything else" link under a truncated list.
 *
 * One part for every such link in the panel, because they are the same offer at
 * two sizes of surface. Sizes whatever mark it is given.
 *
 * **It names no colour.** Which ink an "everything else" offer is set in is a
 * brand's answer and differs between shops, so the call site paints it and this
 * recipe inherits.
 */
export const megamenuMoreVariants = cva(
  [
    'flex items-center gap-1',
    'hover:underline hover:underline-offset-2',
    '[&_svg]:size-5 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        /** Under copy set on the same rhythm. */
        roomy: 'text-sm/6',
        /** Where the rows are tighter. */
        tight: 'text-sm',
      },
    },
  }
);

export type MegamenuMoreProps = ComponentProps<'a'> & {
  readonly variant: 'roomy' | 'tight';
  /** The element the page navigates with. */
  readonly render: RenderElement;
};

export const MegamenuMore = ({ className, variant, render, ...props }: MegamenuMoreProps) =>
  renderThrough(
    render,
    'megamenu-more',
    clsxm(megamenuMoreVariants({ variant }), className),
    props
  );
